/**
 * TopBar — Fixed top navigation bar (48px).
 * LEFT: AWENI logo + current module name
 * RIGHT: notification bell (badge) + profile avatar (initials)
 * Collapses on scroll down, reappears on scroll up.
 */

import type { AweniContext } from '../context/AweniContext';
import { NativeService } from '../services/NativeService';

interface TopBarCallbacks {
  onNotificationsClick: () => void;
  onProfileClick: () => void;
}

let _bar: HTMLElement | null = null;
let _moduleLabel: HTMLSpanElement | null = null;
let _bellBadge: HTMLSpanElement | null = null;
let _avatarBtn: HTMLButtonElement | null = null;
let _ctx: AweniContext | null = null;
let _lastScrollY = 0;
let _hidden = false;

export function mountTopBar(
  container: HTMLElement,
  ctx: AweniContext,
  callbacks: TopBarCallbacks,
): void {
  _ctx = ctx;

  // Sync native status bar style with current theme
  NativeService.setStatusBarStyle(ctx.theme.isDark ? 'dark' : 'light');

  const bar = container;
  bar.setAttribute('role', 'banner');
  bar.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 48px;
    z-index: 100;
    background: var(--topbar-bg);
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    transition: transform 250ms ease;
    box-shadow: var(--shadow-sm);
  `;
  _bar = bar;

  // --- Left: logo + module name ---
  // The brand cluster (logo + wordmark + room separator + room label) is a
  // single clickable region that always navigates to the home lobby — same
  // affordance as Apple's left-edge "Apps" button or Google's app drawer.
  // Users learn this in seconds and rely on it across every module page.
  const left = document.createElement('button');
  left.type = 'button';
  left.setAttribute('aria-label', ctx.i18n.t('nav_home') || 'Home');
  left.style.cssText = `
    display:flex;align-items:center;gap:8px;min-width:0;
    background:transparent;border:none;cursor:pointer;
    padding:4px 8px;margin-inline-start:-8px;border-radius:8px;
    transition:background 120ms ease;
  `;
  left.addEventListener('mouseenter', () => { left.style.background = 'rgba(10,10,10,0.04)'; });
  left.addEventListener('mouseleave', () => { left.style.background = 'transparent'; });
  left.addEventListener('focus', () => { left.style.outline = '2px solid var(--accent, #0A0A0A)'; left.style.outlineOffset = '2px'; });
  left.addEventListener('blur', () => { left.style.outline = 'none'; });
  left.addEventListener('click', () => {
    // Navigate to the home lobby. Use hash so the SPA router handles it
    // without a full page reload (and without re-arming the SW lifecycle).
    if (location.hash !== '#/home') {
      location.hash = '#/home';
    }
  });

  // Brand v1: real monogram image, no wordmark (per docs/BRAND.md). The
  // mark fills ~32px in the 48px topbar — visually equivalent to the old
  // "AWENI" text but reinforces the visual identity instead of repeating
  // the name. The dark theme uses the same PNG: midnight on transparent
  // reads as midnight on cream (light) AND as the silhouette void against
  // dark bg (the cream-on-midnight inverse is the maskable variant for
  // installed icons; in-app we want the literal mark).
  const logo = document.createElement('img');
  logo.src = '/aweni/icons/pwa-192x192.png';
  logo.alt = 'AWENI';
  logo.width = 32;
  logo.height = 32;
  logo.setAttribute('aria-label', 'AWENI');
  logo.style.cssText = `
    display: block;
    flex-shrink: 0;
    border-radius: 6px;
  `;
  // No filter:invert needed \u2014 logo is champagne #9C8348 which reads on both
  // cream (3.4:1) and midnight (5.4:1) backgrounds.

  // Wordmark \u2014 small uppercase AWENI, reinforces brand on every screen.
  const wordmark = document.createElement('span');
  wordmark.textContent = 'AWENI';
  wordmark.style.cssText = `
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 1.5px;
    color: var(--text-primary);
    flex-shrink: 0;
  `;

  const sep = document.createElement('span');
  sep.textContent = '\u00b7';
  sep.style.cssText = 'color:var(--text-secondary);font-size:14px;flex-shrink:0;opacity:0.6;';

  const label = document.createElement('span');
  label.style.cssText = `
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: 0.2px;
  `;
  label.textContent = _resolveRoomName('home', ctx);
  _moduleLabel = label;

  left.append(logo, wordmark, sep, label);

  // --- Right: bell + avatar ---
  const right = document.createElement('div');
  right.style.cssText = 'display:flex;align-items:center;gap:8px;flex-shrink:0;';

  // Notification bell button — WCAG 2.1 AA: stroke + size + hit-target
  // explicitly tuned to pass 1.4.11 non-text 3:1 contrast in BOTH themes.
  // (Was 2px stroke on 20×20 with currentColor inherited from a
  // var(--text-primary) inline style — observed as low-contrast in
  // Firefox light mode.) We now use a class with theme-specific color
  // set in CSS so it's explicit and overridable.
  const bellBtn = document.createElement('button');
  bellBtn.className = 'aweni-topbar-icon-btn';
  bellBtn.setAttribute('aria-label', ctx.i18n.t('topbar_notifications'));
  // NOTE: do NOT set `background: transparent` or `color: ...` inline here —
  // both must come from the .aweni-topbar-icon-btn class so the theme toggle
  // and the .dark prefix actually take effect. An inline `background: transparent`
  // here used to defeat the visible pill bg + made the bell read as white-on-white
  // in light mode (Firefox cascaded the body's text-white into the SVG stroke
  // via `currentColor`).
  bellBtn.style.cssText = `
    position: relative;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    transition: background 120ms ease;
  `;
  bellBtn.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>`;

  const badge = document.createElement('span');
  badge.style.cssText = `
    position: absolute;
    top: 4px;
    right: 4px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background: var(--notification-dot);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: none;
    align-items: center;
    justify-content: center;
    line-height: 1;
  `;
  _bellBadge = badge;
  bellBtn.appendChild(badge);
  bellBtn.addEventListener('click', callbacks.onNotificationsClick);

  // Profile avatar button. The .aweni-topbar-avatar class opts out of the
  // global 44×44 button min-size — that minimum was inflating the avatar to
  // 44×44 in a 48px topbar, leaving 2px of breathing room and making the
  // circle visually clip the topbar's border-bottom into the banner below.
  const avatarBtn = document.createElement('button');
  avatarBtn.className = 'aweni-topbar-avatar';
  avatarBtn.setAttribute('aria-label', ctx.i18n.t('topbar_profile'));
  // The text color MUST be `var(--accent-text)` not `#fff`. Brand v1 makes
  // --accent invert per theme (black in light, cream in dark); a hardcoded
  // white initial reads as cream-on-cream in dark mode → invisible avatar.
  // --accent-text is the paired token that always contrasts with --accent.
  avatarBtn.style.cssText = `
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--accent);
    color: var(--accent-text);
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
    flex-shrink: 0;
  `;
  _updateAvatarText(avatarBtn, ctx);
  _avatarBtn = avatarBtn;
  avatarBtn.addEventListener('click', callbacks.onProfileClick);

  right.append(bellBtn, avatarBtn);
  bar.append(left, right);

  // Initial badge update
  _refreshBadge(ctx);

  // Scroll collapse — watch the main viewport
  const viewport = document.getElementById('aweni-main');
  if (viewport) {
    viewport.addEventListener('scroll', () => {
      const y = viewport.scrollTop;
      if (y > _lastScrollY && y > 48 && !_hidden) {
        _hidden = true;
        if (_bar) _bar.style.transform = 'translateY(-100%)';
      } else if (y < _lastScrollY && _hidden) {
        _hidden = false;
        if (_bar) _bar.style.transform = 'translateY(0)';
      }
      _lastScrollY = y;
    }, { passive: true });
  }
}

/**
 * Update the room name shown in the top bar after the AWENI wordmark.
 * Uses _resolveRoomName which prefers proper nouns (BalanceTab, Memoriz,
 * SelectO, Yawmi) for branded rooms and i18n nav_* labels for the rest.
 */
export function setTopBarModuleName(moduleId: string): void {
  if (_moduleLabel && _ctx) {
    _moduleLabel.textContent = _resolveRoomName(moduleId, _ctx);
  }
}

/**
 * Map a module ID to its display name in the topbar breadcrumb.
 * Proper nouns (BalanceTab, Memoriz, SelectO, Yawmi) stay in Latin script
 * across all 5 languages — they are product names, not translatable words.
 * Generic modules use the i18n nav_* labels.
 */
function _resolveRoomName(moduleId: string, ctx: AweniContext): string {
  // Branded rooms — proper nouns, never translated
  const PROPER_NAMES: Record<string, string> = {
    home: 'Lobby',
    balance: 'BalanceTab',
    money: 'BalanceTab',
    balancetab: 'BalanceTab',
    quran: 'Memoriz',
    memoriz: 'Memoriz',
    selecto: 'SelectO',
    yawmi: 'Yawmi',
  };
  if (PROPER_NAMES[moduleId]) return PROPER_NAMES[moduleId];
  // Fallback to i18n nav_* label, or the module id itself
  const key = `nav_${moduleId}`;
  const translated = ctx.i18n.t(key);
  return translated !== key ? translated : moduleId;
}

/**
 * Refresh the unread notification badge count.
 */
export function refreshTopBarBadge(): void {
  if (_ctx) _refreshBadge(_ctx);
}

/**
 * Refresh the profile avatar initials.
 */
export function refreshTopBarAvatar(): void {
  if (_avatarBtn && _ctx) _updateAvatarText(_avatarBtn, _ctx);
}

function _refreshBadge(ctx: AweniContext): void {
  if (!_bellBadge) return;
  const count = ctx.notifications.unreadCount;
  if (count > 0) {
    _bellBadge.textContent = count > 99 ? '99+' : String(count);
    _bellBadge.style.display = 'flex';
  } else {
    _bellBadge.style.display = 'none';
  }
}

function _updateAvatarText(btn: HTMLButtonElement, ctx: AweniContext): void {
  const profile = ctx.auth.activeProfile;
  const user = ctx.auth.user;
  const name = profile?.name || user?.name || '?';
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2);
  btn.textContent = initials.toUpperCase() || '?';
}
